import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Box,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";
import useTranslation from "hooks/Translation";

export default function OrderDetail({ orderData }) {
  const t = useTranslation();
  const currency = getFromStorage(STORAGE_KEYS.currency);

  const calculateTotal = (quantity, price) => {
    const numericQuantity = parseFloat(quantity.split(" ")[0]);
    return numericQuantity * price;
  };

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "110px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const Name = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left" sx={{ minWidth: "140px" }}>
      <MDTypography display="block" variant="caption" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const columns = [
    { Header: t.main.item, accessor: "item", align: "left" },
    { Header: t.main.quantity, accessor: "quantity", align: "left" },
    { Header: t.main.price, accessor: "price", align: "left" },
    { Header: t.main.total, accessor: "total", align: "left" },
  ];

  const rows = orderData?.product?.map((itx) => ({
    item: <Name title={`${itx?.product_name_en}` || ""} />,
    quantity: (
      <Name
        title={`${itx?.buy_quantity || "-"} ${
          itx?.product_quantity_type === 1 ? "Kg" : "Liter"
        }`}
      />
    ),
    price: (
      <Job
        title={
          `${currency === "USD" ? "USD" : "AED"} ${itx?.price_per_unit}` || "--"
        }
      />
    ),
    total: (
      <Job
        title={`${currency === "USD" ? "USD" : "AED"} ${
          itx?.price_per_unit * itx?.buy_quantity
        }`}
      />
    ),
  }));

  return (
    <Box padding={4}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        {t.main.orderDetails} #{orderData?.order_track_id}
      </Typography>

      <Typography variant="h5" fontWeight="bold" mb={3}>
        {t.main.name} : {orderData?.user || "-"}
      </Typography>

      <Grid container spacing={3}>
        {/* Order Items */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderWidth: 1, borderColor: "grey" }}>
            <CardHeader
              avatar={<ShoppingCartOutlinedIcon />}
              title={t.main.orderItems}
            />
            <CardContent>
              {orderData?.product?.length ? (
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderWidth: 1, borderColor: "grey" }}>
            <CardHeader
              avatar={<ReceiptOutlinedIcon />}
              title={t.main.orderSummary}
            />
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>{t.main.subTotal}:</Typography>
                <Typography>
                  {currency === "USD" ? "USD" : "AED"}{" "}
                  {orderData?.sub_total || "-"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>{t.main.tax}:</Typography>
                <Typography>
                  {currency === "USD" ? "USD" : "AED"}{" "}
                  {orderData?.delivery_fees || "0"}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>{t.main.deliveryFees}:</Typography>
                <Typography>
                  {currency === "USD" ? "USD" : "AED"}{" "}
                  {orderData?.delivery_fees || "0"}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                fontWeight="bold"
              >
                <Typography>{t.main.total}:</Typography>
                <Typography>AED {orderData?.total_cost_pay || "-"}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Delivery Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderWidth: 1, borderColor: "grey" }}>
            <CardHeader
              avatar={<LocalShippingOutlinedIcon />}
              title={t.main.deliveryInformation}
            />
            <CardContent>
              <Typography>
                <strong>{t.main.status}:</strong>{" "}
                {orderData?.order_status === 1
                  ? t.main.orderPlaced
                  : orderData?.order_status === 2
                  ? t.main.inProgress
                  : orderData?.order_status === 3
                  ? t.main.shipping
                  : t.main.delivered}
              </Typography>
              <Typography>
                <strong>{t.main.address}:</strong>{" "}
                {`${orderData?.address?.business_address},${orderData?.address?.city}`}
              </Typography>
              <Typography>
                <strong>{t.main.instructions}:</strong> {"-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
